/**
 * Testi e opzioni del composer (prototipo L254-L302, SPEC §3.11).
 *
 * Lo stato dopo la risposta si sceglie nel popup di conferma dell'invio
 * (decisione del committente, 25/10/2026: al posto della select "Invia e …"
 * accanto al tasto, che i rappresentanti dimenticavano). Le scelte sono SOLO
 * le transizioni valide (`STATUS_TRANSITIONS`, la stessa tabella della select
 * "Cambia stato") più "lascia invariato", e nessuna è preselezionata.
 */
import { STATUS_LABELS, STATUS_TRANSITIONS, type ComposeTarget } from '../constants';

/** Scelta dello stato nel popup di invio. `value` '' = lascia invariato. */
export interface ReplyStatusChoice {
  value: string;
  label: string;
  /** Riga di spiegazione sotto l'etichetta (assente = nessuna). */
  hint?: string;
}

/**
 * Stato in cui la risposta stessa porta il ticket: una risposta del canale
 * promuove da sola `aperto`/`preso_in_carico` a `in_lavorazione` (backend
 * `supportTicketService.reply`). Le opzioni vanno calcolate da QUI, non dallo
 * stato a schermo, altrimenti la select offre "Preso in carico" su un ticket
 * aperto (che il backend rifiuta con 409) e nasconde "Risolto" proprio dove
 * l'operatore risponde e chiude in un colpo solo.
 */
export const statusAfterReply = (status: string): string =>
  status === 'aperto' || status === 'preso_in_carico' ? 'in_lavorazione' : status;

const statusLabel = (s: string): string => STATUS_LABELS[s] || s;

/** Etichetta della scelta che porta il ticket a `to` partendo da `from` (dopo la risposta). */
const choiceLabel = (from: string, to: string): string => {
  if (to === 'in_attesa_associato') return "Invia e metti in attesa di risposta dell'associato";
  if (to === 'risolto') return 'Invia come risolto';
  if (to === 'in_lavorazione') {
    if (from === 'in_attesa_associato') return 'Invia e riporta "In lavorazione"';
    if (from === 'risolto') return 'Invia e riapri ("In lavorazione")';
  }
  return `Invia e metti "${statusLabel(to)}"`;
};

/**
 * Scelte del popup di invio: prima "lascia invariato" (con lo stato in cui la
 * risposta porta comunque il ticket, spiegato se diverso da quello attuale),
 * poi le transizioni ammesse da QUELLO stato. In bozza (`draftReply`) lo stato
 * cambia solo all'approvazione (`writeReply` del backend), e la spiegazione
 * lo dice.
 */
export const replyStatusChoices = (status: string, draftReply = false): ReplyStatusChoice[] => {
  const after = statusAfterReply(status);
  const keep: ReplyStatusChoice = { value: '', label: `Invia lasciando il ticket "${statusLabel(after)}"` };
  if (after !== status) {
    keep.hint = draftReply
      ? `Ora è "${statusLabel(status)}": passerà comunque a "${statusLabel(after)}" quando la bozza viene approvata`
      : `Ora è "${statusLabel(status)}": con la risposta passa comunque a "${statusLabel(after)}"`;
  }
  return [
    keep,
    ...(STATUS_TRANSITIONS[after] || []).map((s) => ({ value: s, label: choiceLabel(after, s) })),
  ];
};

/**
 * Scelta di partenza a ogni apertura del popup: nessuna (`null`, conferma
 * disattivata finché non si sceglie), salvo quando la scelta è una sola.
 */
export const initialReplyStatusChoice = (choices: ReplyStatusChoice[]): string | null =>
  choices.length === 1 ? choices[0].value : null;

/**
 * `draftReply` (Task 46): l'operatore e un componente senza «puo rispondere»
 * (`ticket.viewerCanReply === false`), la sua risposta diventa una bozza che il
 * responsabile del canale deve approvare. Le note interne non cambiano.
 */
export const sendButtonLabel = (mode: ComposeTarget, draftReply = false): string =>
  mode === 'note' ? 'Salva nota' : draftReply ? 'Invia per approvazione' : 'Invia risposta';

/** Avviso sotto il composer in modalita bozza (Task 46). */
export const DRAFT_REPLY_NOTICE =
  "La risposta sarà inviata all'associato dopo l'approvazione del responsabile del canale";

/** La risposta di questo operatore su questo ticket diventa una bozza (Task 46). */
export const isDraftReplyMode = (mode: ComposeTarget, ticket: { viewerCanReply?: boolean }): boolean =>
  mode === 'reply' && ticket.viewerCanReply === false;

/**
 * Canale di smistamento (decisione del committente, 13/10/2026): finche il
 * ticket non e trasferito al canale competente non si risponde all'associato.
 * Il motivo arriva dal backend (`replyBlockedReason`), non si deduce dal canale.
 * Restano note interne, menzioni, trasferimento e «Riassumi il ticket».
 */
export const isReplyBlocked = (ticket: { replyBlockedReason?: string | null }): boolean =>
  ticket.replyBlockedReason === 'da_smistare';

/** Avviso al posto del composer di risposta nel canale di smistamento. */
export const TRIAGE_REPLY_NOTICE =
  "Ticket ancora da smistare. Trasferiscilo al canale competente prima di rispondere all'associato.";

/** Codice del 409 del backend quando si prova a rispondere dallo smistamento. */
export const TRIAGE_ERROR_CODE = 'TICKET_DA_SMISTARE';

export const composerHint = (mode: ComposeTarget): string =>
  mode === 'note' ? 'SOLO INTERNA · NON INVIATA' : 'CTRL+↵ INVIA';

export const composerPlaceholder = (mode: ComposeTarget): string =>
  mode === 'note'
    ? 'Aggiungi una nota interna, visibile solo al gruppo…'
    : "Scrivi una risposta all'associato…";

/** Etichetta della modalita nel segmented (anche nome accessibile del radio). */
export const composerModeLabel = (mode: ComposeTarget): string =>
  mode === 'note' ? 'Nota interna' : "Risposta all'associato";

// ===== @menzioni nelle note interne (Task 48) =====

/** Chi si menziona: basta la coppia attore + il nome inserito nel testo. */
export interface MentionPick {
  actorType: string;
  actorId: string;
  nome: string;
}

/** Testo inserito nella nota per una menzione. */
export const mentionToken = (nome: string): string => `@${nome}`;

/** Lunghezza massima del testo dopo `@` che tiene aperto l'elenco. */
const MENTION_QUERY_MAX = 40;

/**
 * Menzione in corso di scrittura: l'ultima `@` prima del cursore, a inizio testo
 * o dopo uno spazio (cosi un indirizzo email non apre l'elenco), senza a capo
 * fra la `@` e il cursore. `start` e la posizione della `@`, `query` il testo
 * scritto dopo (puo contenere spazi: «Mario R»).
 */
export const findMentionQuery = (text: string, caret: number): { start: number; query: string } | null => {
  const upto = text.slice(0, Math.max(0, caret));
  const at = upto.lastIndexOf('@');
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(upto[at - 1])) return null;
  const query = upto.slice(at + 1);
  if (query.includes('\n') || query.length > MENTION_QUERY_MAX || /^\s/.test(query)) return null;
  return { start: at, query };
};

/** Minuscolo e senza accenti: «Niccolò» si trova scrivendo «nicco». */
const norm = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const sameActor = (a: { actorType: string; actorId: string }, b: { actorType: string; actorId: string }) =>
  a.actorType === b.actorType && String(a.actorId) === String(b.actorId);

/**
 * Elenco filtrato sul testo dopo la `@`: il nome contiene la ricerca (anche a
 * meta parola). Chi e gia menzionato non si ripropone.
 */
export const filterMentionables = <T extends MentionPick>(list: T[], query: string, picked: MentionPick[] = []): T[] => {
  const q = norm(query.trim());
  return list.filter((m) => !picked.some((p) => sameActor(p, m)) && (!q || norm(m.nome).includes(q)));
};

// ===== Invito dall'elenco @ (spec 2026-10-15, parte B) =====

/** Voce fissa in coda all'elenco @ per chi può invitare. */
export const INVITE_MENTION_LABEL = 'Invita rappresentanti o esterni…';

/** Riga dell'elenco @: una persona menzionabile o la voce «Invita…». */
export type MentionMenuEntry<T> = { kind: 'mention'; item: T } | { kind: 'invite' };

/**
 * Elenco @: le persone filtrate (solo componenti, assegnati e partecipanti del
 * ticket) e, se si può invitare, la voce «Invita…» in coda, anche quando il
 * filtro non trova nessuno (è proprio il caso in cui serve).
 */
export const mentionMenuEntries = <T extends MentionPick>(matches: T[], canInvite: boolean): MentionMenuEntry<T>[] => [
  ...matches.map((item) => ({ kind: 'mention' as const, item })),
  ...(canInvite ? [{ kind: 'invite' as const }] : []),
];

/** Sostituisce `@query` con `@Nome Cognome ` e restituisce testo e nuovo cursore. */
export const insertMention = (
  text: string,
  start: number,
  caret: number,
  nome: string
): { text: string; caret: number } => {
  const token = `${mentionToken(nome)} `;
  const after = text.slice(caret).replace(/^ /, '');
  return { text: text.slice(0, start) + token + after, caret: start + token.length };
};

/**
 * Menzioni ancora valide: quelle il cui `@Nome` e ancora nel testo. Cancellare
 * la menzione dal testo la toglie anche dall'invio.
 */
export const activeMentions = <T extends MentionPick>(body: string, mentions: T[]): T[] =>
  mentions.filter((m) => body.includes(mentionToken(m.nome)));

/** Riga sotto i chip: chi e menzionato riceve l'email (secondo le sue preferenze). */
export const mentionNotice = (n: number): string =>
  n === 1 ? 'Verrà avvisato per email' : 'Verranno avvisati per email';

/** Voce del menu «Assistente AI». `kind` dice quale gestore la esegue. */
export interface AiMenuItem {
  key: string;
  label: string;
  kind: 'suggest' | 'summary' | 'draft';
}

export interface AiMenuGroup {
  id: 'ticket' | 'draft';
  /** Intestazione del gruppo nella veste v4 (L483, L485): «Sul ticket» / «Sulla bozza». */
  label: string;
  items: AiMenuItem[];
}

/**
 * Azioni AI divise nelle due famiglie del design v4 (L483-L488, N19): prima
 * quelle che leggono l'intero ticket, poi le rifiniture della bozza. Funzione
 * pura sopra `AI_ACTIONS` (che resta com'e: e condivisa con l'area riservata).
 * L'ordine delle voci e quello del menu v3; un gruppo vuoto non compare.
 */
export const aiMenuGroups = (opts: {
  canSuggest: boolean;
  canSummarize: boolean;
  draftActions: ReadonlyArray<{ action: string; label: string }>;
}): AiMenuGroup[] => {
  const ticketItems: AiMenuItem[] = [
    ...(opts.canSuggest ? [{ key: 'suggest', label: 'Proponi una risposta', kind: 'suggest' as const }] : []),
    ...(opts.canSummarize ? [{ key: 'summary', label: 'Riassumi il ticket', kind: 'summary' as const }] : []),
  ];
  const draftItems: AiMenuItem[] = opts.draftActions.map((a) => ({ key: a.action, label: a.label, kind: 'draft' }));
  return [
    { id: 'ticket' as const, label: 'Sul ticket', items: ticketItems },
    { id: 'draft' as const, label: 'Sulla bozza', items: draftItems },
  ].filter((g) => g.items.length > 0);
};
